import React from "react";
import { customSanitize } from "../../../utils/customSanitize";
import { ExtendedLink } from "@/components/ExtededLink/ExtendedLink";
import { IconGridOptionsType } from "@/types/ModuleOptionsType";
import styles from "./IconGridBox.module.scss";
import { getItemAttributes } from "@/components/_primitives/utils/getItemAttributes";
import { Breakpoint } from "@aleksundshantu/aunds-utils-react/layout";
import { Button } from "@/components/Button/Button";
import { ContentTag } from "@/components/ContentTag/ContentTag";
import { IconGridModuleType } from "@/modules/IconGridModule/IconGridModule";
import { CustomSVG } from "@/components/_primitives/CustomSVG/CustomSVG";
import {
  getBoxPadding,
  replaceExtendedLinkText,
  checkLinkAvailable,
  getShowBoxClasses,
} from "@/utils/infoBoxUtils";
import { IconBoxType } from "./IconGridBoxes";
import Image from "next/image";

type IconGridElementType = Exclude<
  IconGridModuleType["icon_grid"]["elements"],
  false
>[number];

export type IconGridBoxLayoutType = "v1" | "v2";

export type IconGridLayoutOptionsType = {
  title_style: "normal" | "bold";
  icon_align_class: "is-vcentered" | "default";
  flex_direction_class: "is-flex-direction-row" | "is-flex-direction-column";
  row_direction_class: "is-row-normal-desktop" | "is-row-reverse-desktop";
  text_align_class: "has-text-centered" | "has-text-left";
};

export type IconSizeType = {
  mobile: number | "";
  tablet: number | "";
  desktop: number | "";
  fullhd: number | "";
};

type IconGridBoxProps = {
  iconGridElement: IconGridElementType;
  extraClass: string;
  infoBoxOptions: IconGridOptionsType;
  itemWidth?: Record<Breakpoint, string>;
  show_cardbox: boolean;
  show_divider: boolean;
  heading_level: number;
  layoutOptions: IconGridLayoutOptionsType;
  boxType?: IconBoxType;
};

const getIconSize = (icon_size: IconSizeType) => {
  return {
    "--infobox-icon-size-mobile": `${icon_size.mobile}rem`,
    "--infobox-icon-size-tablet": `${icon_size.tablet}rem`,
    "--infobox-icon-size-desktop": `${icon_size.desktop}rem`,
    "--infobox-icon-size-fullhd": `${icon_size.fullhd}rem`,
  };
};

export const IconGridBox = (props: IconGridBoxProps) => {
  const {
    iconGridElement: element,
    infoBoxOptions,
    itemWidth,
    show_cardbox,
    show_divider,
    heading_level,
    layoutOptions,
    boxType,
  } = props;

  const {
    flex_direction_class,
    icon_align_class,
    row_direction_class,
    title_style,
    text_align_class,
  } = layoutOptions ?? {};

  //In Backend we can get both true at the same time, so show_cardbox has priority
  const effectiveShowCardbox = show_cardbox;
  const effectiveShowDivider = show_cardbox ? false : show_divider;

  const { show_extended_link, button_type, box } = infoBoxOptions ?? {};
  const icon_size = infoBoxOptions.icon_size ?? null;

  const title = element.title;
  const link = element.link;
  const link_extended = element.link_extended;

  const { show_border_radius, show_box_shadow, box_padding } = box;

  const showBowClasses = getShowBoxClasses(
    effectiveShowCardbox,
    effectiveShowDivider,
    show_border_radius,
    show_box_shadow,
  );

  const attr = getItemAttributes({
    itemWidth: itemWidth,
    className: `${styles["article"]} column`,
    itemAttrFor: "icongridbox",
  });

  const TITLE_JSX = (
    <>
      {element.tagline && (
        <span
          className={`infobox-tagline is-block`}
          dangerouslySetInnerHTML={{ __html: customSanitize(element.tagline) }}
        />
      )}
      {title &&
        (button_type !== "link-in-title" ||
          !checkLinkAvailable(
            (show_extended_link ? link_extended : link) || false,
            show_extended_link,
          )) && (
          <span
            role="heading"
            aria-level={heading_level}
            className={`infobox-title ${styles["title"]} is-block ${element.content ? "" : "pb-0"}`}
            dangerouslySetInnerHTML={{ __html: customSanitize(title) }}
          />
        )}
    </>
  );

  const IconJSX = (
    <>
      {element.svg && (
        <div className={`infobox-icon ${styles["infobox-icon"]}`}>
          {element.svg.mime_type === "image/svg+xml" && (
            <CustomSVG
              src={element.svg.url}
              wrapper="svg"
              className="is-flex"
              width={"100%"}
              height={"100%"}
              role="img"
              aria-label={`${element.svg.title} icon`}
            />
          )}
          {element.svg.mime_type !== "image/svg+xml" && (
            <Image
              src={element.svg.url}
              alt={element.svg.alt}
              sizes="(max-width: 767px) 96px, 120px"
              height={element.svg.height}
              width={element.svg.width}
            />
          )}
        </div>
      )}
    </>
  );

  const ContentJSX = (
    <div className={`is-flex infobox-content ${styles["infobox-content"]}`}>
      <div className="h-fit">
        {TITLE_JSX}

        {title &&
          !show_extended_link &&
          link &&
          link.url &&
          button_type === "link-in-title" && (
            <Button
              role="button"
              className={`mb-0`}
              type={"secondary"}
              href={link.url}
              target={link.target === "" ? "_self" : link.target}
              aria-label={link.aria_label}
            >
              {link.title}
            </Button>
          )}

        {title &&
          button_type === "link-in-title" &&
          show_extended_link &&
          link_extended && (
            <ExtendedLink
              extendedLink={replaceExtendedLinkText(title, link_extended)}
              btnType={"secondary"}
              extraClass={`mt-0 infobox-title`}
            />
          )}

        {element.content && (
          <ContentTag
            extraClass={`infobox-text`}
            content={element.content ?? ""}
            disablePaddingBottom
          />
        )}
      </div>

      {!show_extended_link &&
        link &&
        link.url &&
        button_type !== "link-in-title" && (
          <Button
            role="button"
            type={button_type}
            href={link.url}
            target={link.target === "" ? "_self" : link.target}
            aria-label={link.aria_label}
          >
            {link.title}
          </Button>
        )}

      {show_extended_link &&
        link_extended &&
        button_type !== "link-in-title" && (
          <ExtendedLink extendedLink={link_extended} btnType={button_type} />
        )}
    </div>
  );

  if (boxType === "team") {
    return (
      <article
        {...attr}
        style={{
          ...attr.style,
          ...(getBoxPadding(box_padding) as React.CSSProperties),
        }}
      >
        <div
          className={`${styles.infobox} ${styles["team-card"]} ${showBowClasses}`}
        >
          {IconJSX}
          <div className={styles["team-body"]}>
            <div className={styles["team-name"]}>
              {element.tagline && (
                <span
                  className={`infobox-tagline is-block`}
                  dangerouslySetInnerHTML={{
                    __html: customSanitize(element.tagline),
                  }}
                />
              )}
              {title && (
                <span
                  role="heading"
                  aria-level={heading_level}
                  className={`infobox-title ${styles["title"]} is-block`}
                  dangerouslySetInnerHTML={{ __html: customSanitize(title) }}
                />
              )}
            </div>
            {element.content && (
              <ContentTag
                extraClass={`infobox-text`}
                content={element.content}
                disablePaddingBottom
              />
            )}
            {((!show_extended_link && link && link.url) ||
              (show_extended_link && link_extended)) &&
              button_type !== "link-in-title" && (
                <div className={styles["team-btn"]}>
                  {!show_extended_link && link && link.url && (
                    <Button
                      role="button"
                      type={button_type}
                      href={link.url}
                      target={link.target === "" ? "_self" : link.target}
                      aria-label={link.aria_label}
                    >
                      {link.title}
                    </Button>
                  )}
                  {show_extended_link && link_extended && (
                    <ExtendedLink
                      extendedLink={link_extended}
                      btnType={button_type}
                    />
                  )}
                </div>
              )}
          </div>
        </div>
      </article>
    );
  }

  return (
    <article
      {...attr}
      style={{
        ...attr.style,
        ...(getBoxPadding(box_padding) as React.CSSProperties),
        ...(icon_size && (getIconSize(icon_size) as React.CSSProperties)),
      }}
    >
      <div
        data-flex-direction={flex_direction_class}
        data-icon-align={icon_align_class}
        data-title-style={title_style}
        data-is-centered={
          text_align_class === "has-text-centered" &&
          flex_direction_class === "is-flex-direction-column"
            ? "true"
            : "false"
        }
        className={`${styles.infobox} ${
          !effectiveShowCardbox && effectiveShowDivider
            ? styles["has-divider"]
            : ""
        } ${showBowClasses}`}
      >
        {row_direction_class === "is-row-normal-desktop" && (
          <>
            {IconJSX}
            {ContentJSX}
          </>
        )}
        {row_direction_class === "is-row-reverse-desktop" && (
          <>
            {ContentJSX}
            {IconJSX}
          </>
        )}
      </div>
    </article>
  );
};
